/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import React, { useContext, useEffect } from "react";
import dynamic from "next/dynamic";
import {
  ImageHelper,
  ThemeContext,
  channelTagsFromMedia,
  useCanWatchAuthContent,
  IMediaModel,
  IConfigurationModel,
} from "@nf/common";

const MediaDetailsCoverImg = dynamic(
  () => import("components/Media/MediaDetailsCoverImg")
);
const PlayInfo = dynamic(() => import("components/PlayInfo"));
const MediaSEO = dynamic(() => import("components/SEO/MediaSEO"));

import styles from "./MediaDetailsScreen.module.scss";
import { useScrollToTopOnMount } from "../../hooks";
import { AppImage } from "../../components/AppImage/AppImage";
import { MediaDetailsTopContent } from "../../components";
import { useSyncCompleted } from "../../hooks";

export interface IMediaDetailsScreenProps {
  configuration?: IConfigurationModel;
  media?: IMediaModel;
  className?: string;
  children?: JSX.Element;
}

export const MediaDetailsScreen = ({
  media,
  children,
  className,
  configuration,
}: IMediaDetailsScreenProps): JSX.Element => {
  const channelContext = useContext(ThemeContext);
  const { isLoggedIn } = useCanWatchAuthContent(media);
  const bannerImageUrl = ImageHelper.getHeroAssetDetailsImageUrl(media?.Images);
  const isAuthProcessCompleted = useSyncCompleted();

  useScrollToTopOnMount?.();

  useEffect(() => {
    if (media && channelContext.onBrandingChange) {
      channelContext.onBrandingChange(channelTagsFromMedia(media));
    }

    return () => {
      // Restoring default branding
      channelContext?.onBrandingChange?.();
    };
  }, [media]);

  return (
    <div className={`${styles.mediaDetails} ${className ? className : ""}`}>
      {media && (
        <>
          <MediaSEO media={media} configuration={configuration} />

          <div className={styles.background}>
            {bannerImageUrl && (
              <div className={styles.backgroundContainer}>
                <AppImage
                  src={bannerImageUrl}
                  alt={media?.Title || ""}
                  fill={true}
                  isLCP
                />
              </div>
            )}
            {isAuthProcessCompleted && <PlayInfo isHighlight media={media} />}

            <MediaDetailsCoverImg
              className={styles.coverImg}
              images={media?.Images}
              altText={media?.Title}
            />
            <MediaDetailsTopContent isLoggedIn={isLoggedIn} media={media} />
          </div>
          {children}
        </>
      )}
    </div>
  );
};

export default MediaDetailsScreen;
